<template>
  <div class="diagnosis-clock">
    <svg
      class="diagnosis-clock-graphic"
      viewBox="0 0 36 36"
      role="img"
      :aria-label="label"
    >
      <circle
        class="clock-face"
        cx="18"
        cy="18"
        r="15"
      />

      <line
        v-for="tick in tickMarks"
        :key="tick.id"
        :x1="tick.x1"
        :y1="tick.y1"
        :x2="tick.x2"
        :y2="tick.y2"
        class="clock-tick"
      />

      <path
        v-if="sectorPath"
        :d="sectorPath"
        class="clock-sector"
      />

      <line
        :x1="18"
        :y1="18"
        :x2="handPoint.x"
        :y2="handPoint.y"
        class="clock-hand"
      />

      <circle
        cx="18"
        cy="18"
        r="1.7"
        class="clock-pin"
      />
    </svg>

    <span class="diagnosis-clock-label">
      <strong>{{ days }} dias</strong> desde o diagnóstico
    </span>
  </div>
</template>

<script>
const CENTER = 18;
const RADIUS = 15;
// Uma volta completa do relógio representa este horizonte de acompanhamento.
const FULL_CYCLE_DAYS = 60;

function polarToCartesian(angleInDegrees, radius) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: CENTER + radius * Math.cos(angleInRadians),
    y: CENTER + radius * Math.sin(angleInRadians)
  };
}

export default {
  props: {
    days: {
      type: Number,
      default: 0
    }
  },
  computed: {
    elapsedFraction() {
      const safeDays = Math.max(this.days, 0);
      return Math.min(safeDays / FULL_CYCLE_DAYS, 1);
    },
    sweepAngle() {
      return this.elapsedFraction * 360;
    },
    sectorPath() {
      const angle = this.sweepAngle;
      if (angle <= 0) return "";
      if (angle >= 360) {
        return `M ${CENTER} ${CENTER - RADIUS} A ${RADIUS} ${RADIUS} 0 1 1 ${CENTER} ${CENTER + RADIUS} A ${RADIUS} ${RADIUS} 0 1 1 ${CENTER} ${CENTER - RADIUS} Z`;
      }
      const sectorStart = polarToCartesian(0, RADIUS);
      const sectorEnd = polarToCartesian(angle, RADIUS);
      const largeArcFlag = angle > 180 ? 1 : 0;
      return `M ${CENTER} ${CENTER} L ${sectorStart.x} ${sectorStart.y} A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${sectorEnd.x} ${sectorEnd.y} Z`;
    },
    handPoint() {
      return polarToCartesian(this.sweepAngle, RADIUS - 2.5);
    },
    tickMarks() {
      return [0, 90, 180, 270].map((angle, index) => {
        const outer = polarToCartesian(angle, RADIUS);
        const inner = polarToCartesian(angle, RADIUS - 2.5);
        return { id: index, x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y };
      });
    },
    label() {
      return `${this.days} dias desde o diagnóstico`;
    }
  }
};
</script>

<style scoped lang="scss">
$lila-dark: #69287c;
$lila-medium: #8e44ad;

.diagnosis-clock {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.diagnosis-clock-graphic {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
}

.clock-face {
  fill: #fff;
  stroke: #d9cce1;
  stroke-width: 1.5;
}

.clock-sector {
  fill: rgba(142, 68, 173, 0.28);
}

.clock-tick {
  stroke: #c3b2cf;
  stroke-width: 1;
  stroke-linecap: round;
}

.clock-hand {
  stroke: $lila-dark;
  stroke-width: 1.6;
  stroke-linecap: round;
}

.clock-pin {
  fill: $lila-dark;
}

.diagnosis-clock-label {
  font-size: 0.78rem;
  color: #6b6b70;
  line-height: 1.2;
}

.diagnosis-clock-label strong {
  color: $lila-dark;
  font-weight: 700;
}
</style>
